import React, { useState, useEffect, useMemo } from 'react';
import { db, doc, getDoc, onSnapshot, collection, getDocs, updateDoc } from '../firebase';
import { useTeam } from '../contexts/TeamContext';
import { useParams } from 'react-router-dom';
import { RadialBarChart, RadialBar, Legend, ResponsiveContainer, PolarAngleAxis } from 'recharts';
import { memberIdFromEmail, normalizeEmail } from '../utils/membership';
import '../App.css';

const getInitials = (name, email) => {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return '??';
};

const hashHue = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) % 360;
  return h;
};

const pluralizeMatches = (n) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'utakmica';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'utakmice';
  return 'utakmica';
};

const SkeletonBlock = ({ w = '100%', h = 20, r = 6, style }) => (
  <span className="skeleton-block" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const mergeLegacyStats = (primary, legacy) => {
  if (!legacy) return primary;
  return {
    ...primary,
    name: legacy.name || primary.name,
    goals: legacy.goals ?? primary.goals ?? 0,
    assists: legacy.assists ?? primary.assists ?? 0,
    matchesPlayed: legacy.matchesPlayed ?? primary.matchesPlayed ?? 0,
    stats:
      legacy.stats && Object.keys(legacy.stats).length > 0
        ? legacy.stats
        : primary.stats || {},
  };
};

const withMergedStats = (data) => {
  const stats = data.stats || {};
  const statDays = Object.values(stats);
  const hasBreakdown = statDays.length > 0;
  const mergedStats = hasBreakdown
    ? statDays.reduce(
        (acc, day) => {
          acc.goals += day.goals || 0;
          acc.assists += day.assists || 0;
          acc.matchesPlayed += day.matchesPlayed || 0;
          return acc;
        },
        {
          goals: 0,
          assists: 0,
          matchesPlayed: 0,
        }
      )
    : {
        goals: data.goals || 0,
        assists: data.assists || 0,
        matchesPlayed: data.matchesPlayed || 0,
      };
  return { ...data, mergedStats };
};

const normalizeName = (value) => (value || '').trim().toLowerCase();
const looksLikeEmail = (value) => (value || '').includes('@');

const pickBestLegacyPlayer = (players, targetEmail, preferredNames) => {
  const normalizedEmail = normalizeEmail(targetEmail);
  const normalizedNames = preferredNames
    .map((name) => normalizeName(name))
    .filter(Boolean);

  const exactEmail = players.find(
    (player) => normalizeEmail(player.email) === normalizedEmail
  );
  if (exactEmail) return exactEmail;

  for (const normalizedName of normalizedNames) {
    const exactName = players.find(
      (player) => normalizeName(player.name) === normalizedName
    );
    if (exactName) return exactName;
  }

  return null;
};

const Profile = ({ user }) => {
  const { teamId, loading: teamLoading } = useTeam();
  const { email } = useParams();
  const [playerStats, setPlayerStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) return;

    const targetEmail = normalizeEmail(email || user?.email);
    if (!targetEmail) return;

    let memberUnsub = null;
    let cancelled = false;
    setLoading(true);

    const preferredDisplayName = (user?.displayName || '').trim();
    const isOwnProfile = normalizeEmail(user?.email) === targetEmail;

    const loadLegacyPlayers = async () => {
      const playersSnap = await getDocs(collection(db, 'teams', teamId, 'players'));
      return playersSnap.docs
        .map((entry) => ({ id: entry.id, ...entry.data() }))
        .filter((entry) => !entry.migratedToMemberId);
    };

    const memberRef = doc(db, 'teams', teamId, 'members', memberIdFromEmail(targetEmail));
    memberUnsub = onSnapshot(memberRef, async (snap) => {
      if (cancelled) return;

      try {
        const legacyPlayers = await loadLegacyPlayers();
        if (cancelled) return;

        if (snap.exists() && !snap.data().removed) {
          const data = snap.data();
          const merged = {
            name: data.displayName || data.name || targetEmail,
            email: data.email || targetEmail,
            goals: data.goals || 0,
            assists: data.assists || 0,
            matchesPlayed: data.matchesPlayed || 0,
            stats: data.stats || {},
          };

          const legacy = pickBestLegacyPlayer(legacyPlayers, targetEmail, [
            preferredDisplayName,
            merged.name,
            data.displayName,
            data.name,
          ]);

          const finalProfile = withMergedStats(mergeLegacyStats(merged, legacy));
          setPlayerStats(finalProfile);
          setLoading(false);

          const shouldRenameMember =
            legacy &&
            isOwnProfile &&
            legacy.name &&
            legacy.name !== data.displayName &&
            (!data.displayName || looksLikeEmail(data.displayName));

          if (shouldRenameMember) {
            updateDoc(memberRef, { displayName: legacy.name }).catch((renameError) => {
              console.warn('Profile nije mogao preimenovati member displayName:', renameError);
            });
          } else if (isOwnProfile && looksLikeEmail(data.displayName) && user?.uid) {
            const resolvedName = preferredDisplayName ||
              await getDoc(doc(db, 'users', user.uid))
                .then(s => (s.exists() ? (s.data().displayName || '') : ''))
                .catch(() => '');
            if (resolvedName && !looksLikeEmail(resolvedName)) {
              updateDoc(memberRef, { displayName: resolvedName }).catch((renameError) => {
                console.warn('Profile nije mogao ažurirati displayName:', renameError);
              });
            }
          }

          return;
        }

        const legacy = pickBestLegacyPlayer(legacyPlayers, targetEmail, [
          preferredDisplayName,
        ]);

        if (legacy) {
          setPlayerStats(withMergedStats({
            name: legacy.name || preferredDisplayName || targetEmail,
            email: targetEmail,
            goals: legacy.goals || 0,
            assists: legacy.assists || 0,
            matchesPlayed: legacy.matchesPlayed || 0,
            stats: legacy.stats || {},
          }));
        } else {
          setPlayerStats(null);
        }
      } catch (err) {
        console.warn('Profile nije mogao učitati legacy podatke:', err);
        setPlayerStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      if (memberUnsub) memberUnsub();
    };
  }, [email, teamId, user]);

  const derived = useMemo(() => {
    if (!playerStats) return null;
    const goals = playerStats.mergedStats?.goals || 0;
    const assists = playerStats.mergedStats?.assists || 0;
    const matches = playerStats.mergedStats?.matchesPlayed || 0;
    const safeMatches = matches || 1;
    const avgGoals = goals / safeMatches;
    const avgAssists = assists / safeMatches;
    const contributions = goals + assists;
    const gPerM = matches ? (goals / matches).toFixed(2) : '0.00';
    const aPerM = matches ? (assists / matches).toFixed(2) : '0.00';
    const cPerM = matches ? (contributions / matches).toFixed(2) : '0.00';

    const maxVal = Math.max(goals, assists, matches, 1);
    const chartData = [
      { name: 'Utakmice', value: Math.round((matches / maxVal) * 100), raw: matches, fill: '#38bdf8' },
      { name: 'Asistencije', value: Math.round((assists / maxVal) * 100), raw: assists, fill: '#fbbf24' },
      { name: 'Golovi', value: Math.round((goals / maxVal) * 100), raw: goals, fill: '#00f5a0' },
    ];

    return {
      goals,
      assists,
      matches,
      avgGoals,
      avgAssists,
      contributions,
      gPerM,
      aPerM,
      cPerM,
      chartData,
    };
  }, [playerStats]);

  if (teamLoading || loading) {
    return (
      <div className="profile-container">
        <div className="profile-hero skeleton-hero">
          <div className="profile-avatar skeleton-shimmer" />
          <div className="profile-hero-text">
            <SkeletonBlock w="60%" h={28} />
            <SkeletonBlock w="40%" h={14} style={{ marginTop: 10 }} />
          </div>
        </div>
        <div className="stats-grid">
          {[0, 1, 2].map((i) => (
            <div key={i} className="stat-card stat-card--hero">
              <SkeletonBlock w={32} h={32} r={8} />
              <SkeletonBlock w="50%" h={32} style={{ marginTop: 12 }} />
              <SkeletonBlock w="70%" h={10} style={{ marginTop: 8 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!playerStats) {
    return (
      <div className="profile-container">
        <div className="profile-empty">
          <div className="profile-empty-icon">
            <span role="img" aria-label="Upozorenje">⚠</span>
          </div>
          <h3>Nema podataka za ovog igrača</h3>
          <p>Provjeri je li igrač dodan u momčad.</p>
        </div>
      </div>
    );
  }

  const initials = getInitials(playerStats.name, playerStats.email);
  const hue = hashHue(playerStats.email || playerStats.name || '');

  return (
    <div className="profile-container">
      <div className="profile-hero">
        <div
          className="profile-avatar"
          style={{
            background: `linear-gradient(135deg, hsl(${hue} 85% 55%) 0%, hsl(${(hue + 40) % 360} 85% 45%) 100%)`,
          }}
          aria-hidden="true"
        >
          <span>{initials}</span>
        </div>
        <div className="profile-hero-text">
          <div className="profile-eyebrow">Profil igrača</div>
          <h1 className="profile-name">{playerStats.name || 'Nepoznat igrač'}</h1>
          <div className="profile-email">{playerStats.email}</div>
          <div className="profile-chips">
            <span className="profile-chip">
              <span className="chip-dot" /> {derived.matches} {pluralizeMatches(derived.matches)}
            </span>
            <span className="profile-chip">{derived.contributions} G+A</span>
          </div>
        </div>
      </div>

      <div className="stats-grid stats-grid--hero">
        <div className="stat-card stat-card--hero stat-card--accent">
          <div className="stat-card-icon">
            <span role="img" aria-label="Golovi">⚽</span>
          </div>
          <h3 className="stat-number">{derived.goals}</h3>
          <p>Golovi</p>
          <span className="stat-sub">{derived.gPerM} po utakmici</span>
        </div>
        <div className="stat-card stat-card--hero">
          <div className="stat-card-icon">
            <span role="img" aria-label="Asistencije">🎯</span>
          </div>
          <h3 className="stat-number">{derived.assists}</h3>
          <p>Asistencije</p>
          <span className="stat-sub">{derived.aPerM} po utakmici</span>
        </div>
        <div className="stat-card stat-card--hero">
          <div className="stat-card-icon">
            <span role="img" aria-label="Utakmice">🏟️</span>
          </div>
          <h3 className="stat-number">{derived.matches}</h3>
          <p>Utakmice</p>
          <span className="stat-sub">{derived.cPerM} G+A / utakmici</span>
        </div>
      </div>

      <div className="profile-section">
        <div className="profile-section-head">
          <h4>Prosjeci</h4>
          <span className="profile-section-sub">po odigranoj utakmici</span>
        </div>
        <div className="stats-grid stats-grid--secondary">
          <div className="stat-card stat-card--compact">
            <div className="stat-card-row">
              <span className="stat-label">Golovi / utakmici</span>
              <span className="stat-pill stat-pill--accent">{derived.avgGoals.toFixed(2)}</span>
            </div>
            <div className="stat-bar">
              <div
                className="stat-bar-fill stat-bar-fill--accent"
                style={{ width: `${Math.min(derived.avgGoals * 50, 100)}%` }}
              />
            </div>
          </div>
          <div className="stat-card stat-card--compact">
            <div className="stat-card-row">
              <span className="stat-label">Asistencije / utakmici</span>
              <span className="stat-pill stat-pill--info">{derived.avgAssists.toFixed(2)}</span>
            </div>
            <div className="stat-bar">
              <div
                className="stat-bar-fill stat-bar-fill--info"
                style={{ width: `${Math.min(derived.avgAssists * 50, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="profile-section">
        <div className="profile-section-head">
          <h4>Pregled statistike</h4>
          <span className="profile-section-sub">relativni odnos golova, asistencija i utakmica</span>
        </div>
        <div className="chart-container chart-container--radial">
          <ResponsiveContainer width="100%" height={280}>
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="30%"
              outerRadius="100%"
              data={derived.chartData}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar
                background={{ fill: '#1e2732' }}
                dataKey="value"
                cornerRadius={8}
                label={{
                  position: 'insideStart',
                  fill: '#05070a',
                  fontSize: 11,
                  fontWeight: 700,
                  formatter: (v, _name, entry) => (entry?.payload?.raw ?? v),
                }}
              />
              <Legend
                iconSize={8}
                iconType="circle"
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{ fontSize: '0.75rem', color: '#94a3b8', paddingTop: 12 }}
              />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Profile;
